import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { containerRowTemplate, dockReceiptCargoDescription, dockReceiptInitialValues } from "./constant";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { motion } from "framer-motion";
import {
    FileText, User, Ship, Package, MapPin,
    ChevronDown, ChevronUp, Check, Printer, X,
    Save, RotateCcw, Anchor, BookOpen, Send
} from "lucide-react";

import { getDockReceipt, updateDockReceipt } from "@/redux/Booking/actions";
import { callLoader } from "@/redux/Loader/actions";
import { showToast } from "@/redux/Toast/actions";
import FileInformation from "./FileInformation";
import ShipperConsignee from "./ShipperConsignee";
import RoutingInformation from "./RoutingInformation";
import ParticularsFurnished from "./ParticularsFurnished";
import { deepClone } from "@/utility/CommonConstant";
import LCorManual from "./LCorManual";
import PrintDocumentsModal from "./PrintDocumentsModal";
import PageHeader from "@/components/FormHeader/formPageHeader";
import { getEntitySuggestionSearchAPI, ENTITY_SUGGESTION_TYPES } from "@/redux/Common/actions";

// ─── Section colour palette (mirrors NewRateQuote) ───────────────────────────
const SECTION_COLORS = {
    blue: "bg-blue-500",
    cyan: "bg-cyan-500",
    violet: "bg-violet-500",
    purple: "bg-purple-500",
    orange: "bg-orange-500",
    teal: "bg-teal-500",
    green: "bg-emerald-500",
    indigo: "bg-indigo-500",
    slate: "bg-slate-500",
};

// ─── Reusable SectionHeader (identical pattern to NewRateQuote) ───────────────
const SectionHeader = ({ icon: Icon, title, color = "blue", section, badge, expandedSections, toggleSection }) => {
    const iconBg = SECTION_COLORS[color] ?? SECTION_COLORS.blue;
    return (
        <button
            onClick={() => toggleSection(section)}
            className="w-full flex items-center justify-between p-4 border-b bg-gradient-to-r to-slate-100 dark:from-slate-800 dark:to-slate-700 cursor-pointer"
        >
            <div className="flex items-center gap-3">
                <div className={`p-2 mx-2 rounded-lg shadow-md ${iconBg}`}>
                    <Icon className="w-4 h-4 text-white" />
                </div>
                <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-200">
                    {title}
                </h2>
                {badge && (
                    <span className="px-2 py-0.5 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400 text-xs font-medium rounded-full">
                        {badge}
                    </span>
                )}
            </div>
            <div
                className={`p-1.5 rounded-md transition-colors ${expandedSections[section]
                        ? "bg-indigo-100 dark:bg-indigo-900/30"
                        : "bg-slate-200 dark:bg-slate-700"
                    }`}
            >
                {expandedSections[section] ? (
                    <ChevronUp className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                ) : (
                    <ChevronDown className="w-4 h-4 text-slate-500 dark:text-slate-400" />
                )}
            </div>
        </button>
    );
};

// ─── Card wrapper (identical to NewRateQuote section cards) ───────────────────
const SectionCard = React.forwardRef(({ children, className = "" }, ref) => (
    <div
        ref={ref}
        className={`bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-gray-700/30 dark:border-slate-700 overflow-hidden hover:shadow-md dark:shadow-none dark:hover:shadow-[0_0_12px_rgba(59,130,246,0.25)] transition-shadow duration-200 ${className}`}
    >
        {children}
    </div>
));

// ─── Main component ───────────────────────────────────────────────────────────
function DockReceipt() {
    const [formData, setFormData] = useState({ ...dockReceiptInitialValues });
    const [previousData, setPreviousData] = useState({ ...dockReceiptInitialValues });
    const [openPrintModel, setOpenPrintModel] = useState(false);
    const [currentStep, setCurrentStep] = useState(0);

    const [errors, setErrors] = useState({});

    const location = useLocation();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const searchParams = new URLSearchParams(location.search);
    const navState = location.state || {};
    const FILENUMBER =
        navState.FILENUMBER ?? searchParams.get("fileNumber") ?? undefined;
    const { carrierBookingNumber, customerReference } = navState;
    const { bookingId } = useParams();

    // ── Section refs for scroll-spy (same pattern as NewRateQuote) ─────────────
    const fileInfoRef = useRef(null);
    const shipperRef = useRef(null);
    const routingRef = useRef(null);
    const particularsRef = useRef(null);
    const lcOrManualRef = useRef(null);

    const stepSectionMap = useMemo(
        () => ["fileInfo", "shipperConsignee", "routing", "particulars", "LCorManual"],
        []
    );

    const sectionRefs = useMemo(() => ({
        fileInfo: fileInfoRef,
        shipperConsignee: shipperRef,
        routing: routingRef,
        particulars: particularsRef,
        LCorManual: lcOrManualRef,
    }), []);

    const steps = ["File Info", "Shipper/Consignee", "Routing", "Particulars", "Containers"];

    // ── Expanded state ─────────────────────────────────────────────────────────
    const [expandedSections, setExpandedSections] = useState({
        fileInfo: true,
        shipperConsignee: true,
        forwardingAgent: true,
        routing: true,
        particulars: true,
        LCorManual: true,
    });

    const toggleSection = (section) =>
        setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));

    const scrollToSection = (index) => {
        const key = stepSectionMap[index];
        const ref = sectionRefs[key];
        if (ref?.current) {
            ref.current.scrollIntoView({ behavior: "smooth", block: "start" });
            setExpandedSections((prev) => ({ ...prev, [key]: true }));
        }
    };

    // ── IntersectionObserver scroll-spy (mirrors NewRateQuote) ────────────────
    useEffect(() => {
        const observers = [];
        stepSectionMap.forEach((key, index) => {
            const ref = sectionRefs[key];
            if (!ref?.current) return;
            const observer = new IntersectionObserver(
                ([entry]) => {
                    if (entry.isIntersecting) setCurrentStep(index);
                },
                { root: null, rootMargin: "-30% 0px -60% 0px", threshold: 0 }
            );
            observer.observe(ref.current);
            observers.push(observer);
        });
        return () => observers.forEach((o) => o.disconnect());
    }, [formData, stepSectionMap, sectionRefs]);

    // ── isSectionFilled (for progress-bar colouring) ──────────────────────────
    const isSectionFilled = useCallback(
        (key) => {
            if (!formData) return false;
            switch (key) {
                case "fileInfo":
                    return true;
                case "shipperConsignee":
                    return !!(formData?.shipper)
                case "routing":
                    return true;
                case "particulars":
                    return true;
                case "LCorManual":
                    return true;
                default:
                    return false;
            }
        },
        [formData]
    );

    // ── Data fetching / updating ───────────────────────────────────────────────
    useEffect(() => {
        if (bookingId != 0 && bookingId != null && bookingId != undefined) {
            _getDockReceipt();
        }
    }, [bookingId]);

    const _getDockReceipt = async () => {
        try {
            dispatch(callLoader(1));
            const resultData = await dispatch(getDockReceipt(bookingId));
            if (!resultData?.dockReceiptContainer?.length) {
                resultData.dockReceiptContainer = [{ ...containerRowTemplate }];
            }
            if (!resultData?.dockReceiptCargoDescription?.length) {
                resultData.dockReceiptCargoDescription = dockReceiptCargoDescription;
            }
            setFormData({ ...resultData });
            setPreviousData(deepClone(resultData));
        } catch (error) {
            dispatch(showToast("Failed to fetch dock receipt.", "error"));
        } finally {
            dispatch(callLoader(0));
        }
    };



    // Update _updateDockReceipt to validate before saving:
    const _updateDockReceipt = async () => {
        // ── Validate required fields ──────────────────────────────
        const newErrors = {};
        if (!formData?.shipper || formData.shipper.trim() === "") {
            newErrors.shipper = "Shipper / Exporter is required.";
        }
        // Add more fields here as needed, e.g.:
        // if (!formData?.consignee) newErrors.consignee = "Consignee is required.";

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            // Scroll to shipper section so the user sees the error
            shipperRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
            setExpandedSections((prev) => ({ ...prev, shipperConsignee: true }));
            dispatch(showToast("Please fill in all required fields.", "error"));
            return;
        }

        setErrors({});  // clear on success

        try {
            const payload = { ...formData };
            dispatch(callLoader(1));
            const resultData = await dispatch(updateDockReceipt(payload));
            dispatch(showToast(resultData, "success", true));
        } catch (error) {
            dispatch(showToast("Dock Receipt update failed.", "error"));
        } finally {
            dispatch(callLoader(0));
        }
    };

    // Pass errors and setErrors into ShipperConsignee:


    const handleChnage = (key, value) =>
        setFormData((pre) => ({ ...pre, [key]: value }));

    // ── Container helpers (unchanged logic) ───────────────────────────────────
    const calculateTotals = (containers = []) => ({
        totalcontainer: containers.length || 0,
        totalpackage: containers.reduce((s, c) => s + (parseInt(c?.nopackage) || 0), 0),
        totalkgs: containers.reduce((s, c) => s + (parseFloat(c?.kgs) || 0), 0).toFixed(3),
        totallbs: containers.reduce((s, c) => s + (parseFloat(c?.lbs) || 0), 0).toFixed(3),
        totalcbm: containers.reduce((s, c) => s + (parseFloat(c?.cbm) || 0), 0).toFixed(3),
        totalcft: containers.reduce((s, c) => s + (parseFloat(c?.cft) || 0), 0).toFixed(3),
    });

    const updateContainer = (key, value, index, type) => {
        console.log("1. Called:", key, value, type);

        if (type === "number" && !/^\d*\.?\d*$/.test(value)) {
            console.log("2. BLOCKED by regex");
            return;
        }

        const KG_TO_LBS = 2.2046226218;
        const CBM_TO_CFT = 35.314667;
        const num = parseFloat(value) || 0;

        setFormData((prev) => {
            console.log("3. Inside setFormData, notautocalculate:", prev?.notautocalculate);
            const dockReceiptContainer = JSON.parse(JSON.stringify(prev.dockReceiptContainer));

            dockReceiptContainer[index][key] = value;

            if (!prev?.notautocalculate) {
                console.log("4. Running conversion for key:", key, "num:", num);
                if (key === "netkgs") { dockReceiptContainer[index].netlbs = Number((num * KG_TO_LBS).toFixed(3)); console.log("5. Set netlbs:", dockReceiptContainer[index].netlbs); }
                if (key === "netlbs") { dockReceiptContainer[index].netkgs = Number((num / KG_TO_LBS).toFixed(3)); }
                if (key === "kgs") { dockReceiptContainer[index].lbs = Number((num * KG_TO_LBS).toFixed(3)); console.log("5. Set lbs:", dockReceiptContainer[index].lbs); }
                if (key === "lbs") { dockReceiptContainer[index].kgs = Number((num / KG_TO_LBS).toFixed(3)); }
                if (key === "cbm") { dockReceiptContainer[index].cft = Number((num * CBM_TO_CFT).toFixed(3)); }
                if (key === "cft") { dockReceiptContainer[index].cbm = Number((num / CBM_TO_CFT).toFixed(3)); }
            } else {
                console.log("4. SKIPPED conversion - notautocalculate is TRUE");
            }

            const totals = calculateTotals(dockReceiptContainer);
            return { ...prev, dockReceiptContainer, ...totals };
        });
    };
    const UpdatedockReceiptCargoDescription = (key, value) => {
        const dockReceiptContainer = formData?.dockReceiptCargoDescription[0];
        const update = { ...dockReceiptContainer, [key]: value };
        setFormData((pre) => ({ ...pre, dockReceiptCargoDescription: [update] }));
    };

    const addContainer = () => {
        const dockReceiptContainer = deepClone(formData.dockReceiptContainer);
        dockReceiptContainer.push(containerRowTemplate);
        setFormData((pre) => ({ ...pre, dockReceiptContainer }));
    };

    const removeContainer = (index) => {
        const dockReceiptContainer = deepClone(formData.dockReceiptContainer);
        dockReceiptContainer.splice(index, 1);
        setFormData((prev) => ({ ...prev, dockReceiptContainer }));
    };

    const onReset = () => setFormData(deepClone(previousData));

    // ── Shared input / select class strings (same as DockReceipt original) ────
    const inputClass =
        "w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 disabled:cursor-not-allowed disabled:bg-slate-100 dark:disabled:bg-slate-700 disabled:opacity-70 transition-all";
    const selectClass =
        "w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 disabled:cursor-not-allowed disabled:bg-slate-100 dark:disabled:bg-slate-700 disabled:opacity-70 transition-all appearance-none cursor-pointer";
    const labelClass =
        "block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1";

    // ─────────────────────────────────────────────────────────────────────────
    return (
        <>
            {/* ── PageHeader ── */}
            <div className="page-header-sticky sticky top-0 z-50">
                <PageHeader
                    icon={<Anchor className="w-5 h-5 text-white" />}
                    title="Dock Receipt"

                    subtitle={
                        FILENUMBER ? (
                            <>
                                {"File No. "}
                                <span className="text-slate-700 font-semibold dark:text-slate-300">
                                    {FILENUMBER}
                                </span>
                            </>
                        ) : (
                            "Manage dock receipt details"
                        )
                    }
                    actions={[
                        {
                            label: "Cancel",
                            icon: <X />,
                            onClick: () => navigate(-1),
                            variant: "ghost",
                        },
                        {
                            label: "Print",
                            icon: <Printer />,
                            onClick: (e) => {
                                e.stopPropagation();
                                const encodedData = btoa(bookingId);
                                window.open(`/dock-receipt/print/${encodedData}`, "_blank");
                            },
                            variant: "tertiary",
                        },
                        "|",
                        {
                            label: "HBL",
                            onClick: (e) => {
                                e.stopPropagation();
                                navigate("/hbl", {
                                    state: {
                                        bookingId: bookingId,
                                        FILENUMBER: FILENUMBER,
                                    },
                                })
                            }
                            ,
                        },
                        {
                            label: "MBL",
                            onClick: (e) => {
                                e.stopPropagation();
                                navigate("/mbl", {
                                    state: {
                                        bookingId: bookingId,
                                        FILENUMBER: FILENUMBER,
                                    },
                                })
                            }
                        },
                        {
                            label: "Status Audit",
                            variant: "black",
                            onClick: (e) => {
                                e.stopPropagation();
                                navigate("/status-audit", {
                                    state: {
                                        bookingId: bookingId,
                                        FILENUMBER: FILENUMBER,
                                    },
                                })
                            }
                        }

                    ]}
                    onSubmit={_updateDockReceipt}
                    submitLabel="Submit Receipt"
                />
                <div style={{ height: "var(--page-header-height)" }} />
            </div>
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="min-h-screen bg-slate-50 dark:bg-slate-900"
            >


                <div className="p-4 space-y-3 pb-16">

                    {/* ── Progress Bar (full IntersectionObserver logic) ── */}
                    <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-gray-700/30 dark:border-slate-700 px-4 py-3 overflow-x-auto">
                     <div className="flex items-center w-full justify-between gap-0">
    {steps.map((step, index) => {
        const sectionKey = stepSectionMap[index];
        if (sectionKey === "LCorManual" && !formData?.islcManualdetails) return null;

        const filled = isSectionFilled(sectionKey);
        const done = filled && index !== currentStep;
        const active = index === currentStep;

        return (
            // ✅ Fragment instead of <div className="flex items-center">
            <React.Fragment key={step}>
                <div
                    className={[
                        "flex items-center gap-2 h-8 pl-2 pr-3 rounded-full text-xs font-medium cursor-pointer transition-all duration-200 select-none whitespace-nowrap",
                        done && "text-green-700 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20",
                        active && "bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300",
                        !done && !active && "text-slate-400 dark:text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800",
                    ].filter(Boolean).join(" ")}
                    onClick={() => scrollToSection(index)}
                >
                    <span
                        className={[
                            "w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-semibold flex-shrink-0",
                            done && "bg-green-500 text-white",
                            active && "bg-blue-600 text-white",
                            !done && !active && "bg-slate-200 dark:bg-slate-700 text-slate-500 dark:text-slate-400",
                        ].filter(Boolean).join(" ")}
                    >
                        {done ? <Check className="w-3 h-3" /> : index + 1}
                    </span>
                    <span className="hidden sm:inline">{step}</span>
                </div>

                {index < steps.length - 1 && (
                    <div
                        className={[
                            "flex-1 h-0.5 min-w-[8px] mx-1 rounded-full transition-colors duration-300",
                            done ? "bg-green-400 dark:bg-green-600" : "bg-slate-200 dark:bg-slate-700",
                        ].join(" ")}
                    />
                )}
            </React.Fragment>
        );
    })}
</div>
                    </div>

                    {/* ── 1. File Information ── */}
                    <SectionCard ref={fileInfoRef}>
                        <SectionHeader
                            icon={FileText}
                            title="File Information"
                            color="blue"
                            section="fileInfo"
                            expandedSections={expandedSections}
                            toggleSection={toggleSection}
                        />
                        {expandedSections.fileInfo && (
                            <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: "auto", opacity: 1 }}
                                className="p-3"
                            >
                                <FileInformation


                                    formData={formData}
                                    inputClass={inputClass}
                                    labelClass={labelClass}
                                    fileNumber={FILENUMBER}
                                    carrierBookingNumber={carrierBookingNumber}
                                    customerReference={customerReference}
                                    onChange={handleChnage}
                                />
                            </motion.div>
                        )}
                    </SectionCard>

                    {/* ── 2. Shipper / Consignee ── */}
                    <SectionCard ref={shipperRef}>
                        <SectionHeader
                            icon={User}
                            title="Shipper & Consignee"
                            color="cyan"
                            section="shipperConsignee"
                            expandedSections={expandedSections}
                            toggleSection={toggleSection}
                        />
                        {expandedSections.shipperConsignee && (
                            <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: "auto", opacity: 1 }}
                                className="p-3"
                            >
                                <ShipperConsignee
                                    formData={formData}
                                    inputClass={inputClass}
                                    labelClass={labelClass}
                                    onChange={handleChnage}
                                    errors={errors}
                                    setErrors={setErrors}
                                />
                            </motion.div>
                        )}
                    </SectionCard>

                    {/* ── 3. Routing Information ── */}
                    <SectionCard ref={routingRef}>
                        <SectionHeader
                            icon={MapPin}
                            title="Routing Information"
                            color="purple"
                            section="routing"
                            expandedSections={expandedSections}
                            toggleSection={toggleSection}
                        />
                        {expandedSections.routing && (
                            <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: "auto", opacity: 1 }}
                                className="p-3"
                            >
                                <RoutingInformation

                                    formData={formData}
                                    inputClass={inputClass}
                                    labelClass={labelClass}
                                    selectClass={selectClass}
                                    onChange={handleChnage}
                                />
                            </motion.div>
                        )}
                    </SectionCard>

                    {/* ── 4. Particulars Furnished ── */}
                    <SectionCard ref={particularsRef}>
                        <SectionHeader
                            icon={Package}
                            title="Particulars Furnished by Shipper"
                            color="orange"
                            section="particulars"
                            badge={
                                formData?.dockReceiptContainer?.length
                                    ? `${formData.dockReceiptContainer.length} container${formData.dockReceiptContainer.length > 1 ? "s" : ""}`
                                    : undefined
                            }
                            expandedSections={expandedSections}
                            toggleSection={toggleSection}
                        />
                        {expandedSections.particulars && (
                            <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: "auto", opacity: 1 }}
                                className="p-3"
                            >
                                <ParticularsFurnished

                                    formData={formData}
                                    inputClass={inputClass}
                                    selectClass={selectClass}
                                    labelClass={labelClass}
                                    onChange={handleChnage}
                                    updateContainer={updateContainer}
                                    removeContainer={removeContainer}
                                    addContainer={addContainer}
                                />
                            </motion.div>
                        )}
                    </SectionCard>

                    {/* ── 5. LC or Manual (conditional) ── */}
                    {formData?.islcManualdetails && (
                        <SectionCard ref={lcOrManualRef}>
                            <SectionHeader
                                icon={BookOpen}
                                title="LC / Manual Details"
                                color="teal"
                                section="LCorManual"
                                expandedSections={expandedSections}
                                toggleSection={toggleSection}
                            />
                            {expandedSections.LCorManual && (
                                <motion.div
                                    initial={{ height: 0, opacity: 0 }}
                                    animate={{ height: "auto", opacity: 1 }}
                                    className="p-3"
                                >
                                    <LCorManual

                                        formData={formData}
                                        inputClass={inputClass}
                                        labelClass={labelClass}
                                        onChange={UpdatedockReceiptCargoDescription}
                                    />
                                </motion.div>
                            )}
                        </SectionCard>
                    )}


                    <div className="p-3 bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-gray-200 dark:border-slate-800 overflow-hidden hover:shadow-md dark:shadow-none dark:hover:shadow-[0_0_12px_rgba(59,130,246,0.25)] transition-shadow duration-200">
                        <div className="flex items-center justify-between">
                            <div className="text-sm text-slate-500 dark:text-slate-400">
                                <span className="text-red-500">*</span> Required fields must be filled
                            </div>
                            <div className="flex items-center gap-1.5">
                                {/* Cancel */}
                                <button
                                    type="button"
                                    onClick={() => navigate(-1)}
                                    className="inline-flex items-center gap-1.5 text-[12.5px] font-medium px-4 h-[40px] rounded-[9px] transition-all duration-150 text-slate-600 hover:bg-red-50 dark:text-slate-300 dark:hover:bg-red-900/20"
                                >
                                    <X className="w-[15px] h-[15px]" />
                                    <span className="hidden sm:inline">Cancel</span>
                                </button>

                                {/* Print */}
                                <button
                                    type="button"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        const encodedData = btoa(bookingId);
                                        window.open(`/dock-receipt/print/${encodedData}`, "_blank");
                                    }}
                                    className="inline-flex items-center gap-1.5 text-[12.5px] font-medium px-4 h-[40px] rounded-[9px] transition-all duration-150 bg-slate-100 text-slate-600 border border-slate-200 hover:bg-slate-200 dark:bg-slate-700 dark:text-slate-200 dark:border-slate-600 dark:hover:bg-slate-600"
                                >
                                    <Printer className="w-[15px] h-[15px]" />
                                    <span className="hidden sm:inline">Print</span>
                                </button>


                                <div className="w-px h-6 bg-slate-200 dark:bg-slate-700 mx-1" />

                                {/* Save / Submit */}
                                <button
                                    type="button"
                                    onClick={_updateDockReceipt}
                                    className="inline-flex items-center gap-1.5 text-[12.5px] font-medium px-5 h-[40px] rounded-[9px] transition-all duration-150 bg-green-600 text-white hover:bg-green-700 shadow-sm hover:shadow-green-500/30"
                                >
                                    <Send className="w-[15px] h-[15px]" />
                                    <span>Submit Receipt</span>
                                </button>
                            </div>
                        </div>
                    </div>

                </div>

                {/* Print Documents Modal (unchanged) */}
                <PrintDocumentsModal
                    isOpen={openPrintModel}
                    onClose={() => setOpenPrintModel(false)}
                />
            </motion.div>
        </>
    );
}

export default DockReceipt;